// views/Geradoras/Analise/Interrupcoes.tsx
//
// Bloco "Interrupções e horas paradas" da aba Análise (solicitação #6237).
//
// Responde "por que esta usina gerou menos?" com o que a telemetria viu —
// parada, operação parcial, falta de comunicação, falta de leitura — e deixa
// uma pessoa dizer o MOTIVO de cada episódio, ou registrar à mão o que o
// sistema não enxerga.
//
// A detecção é da RPC get_interrupcoes_geradoras e não é gravada: o que este
// bloco grava é só o registro humano (motivo, observação, interrupção manual).
//
// A tabela "Por que gerou menos" fecha a #6216: de quanto cada usina ficou
// abaixo da melhor irmã, quanto as interrupções explicam e quanto sobra.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    CircleHelp, CloudOff, Pencil, Plus, PowerOff, RefreshCw, Siren, Trash2, TrendingDown, TriangleAlert, WifiOff, X,
} from 'lucide-react';
import { api } from '../../../src/services/api';
import { buscarCatalogo, indexarCatalogo, tituloAlarme } from '../../../src/lib/alarmesGeradoras';
import type {
    AlarmeCatalogo, InterrupcaoDetectada, RegistroInterrupcao, ResumoGeracaoUsina, TipoInterrupcao, UnidadeGeradora,
} from '../../../types';
import { formatKwh } from '../geradorasShared';
import type { Janela } from '../geradorasSeries';
import {
    MOTIVOS_INTERRUPCAO, TIPO_INTERRUPCAO, casarRegistros, contaComoPerda, decomporDiferenca, formatHoras, getMotivo,
    resumirPorUsina, rotuloDuracao, rotuloInstante, rotuloPeriodo,
    type LinhaDecomposicao, type LinhaInterrupcao, type MotivoSemComparacao, type ResumoInterrupcoesUsina,
} from '../interrupcoes';

interface Props {
    unidades: UnidadeGeradora[];
    /** Usina escolhida no seletor da aba; null = todas. */
    site: string | null;
    janela: Janela;
    /**
     * Geração de TODAS as usinas no mesmo período — a que a aba já carrega
     * para o ranking. Vazio enquanto carrega: a tabela sai sem a comparação.
     */
    geracao: ResumoGeracaoUsina[];
    /** Mesmo rótulo do seletor e do ranking. */
    nomePorSite: Record<string, string>;
    /**
     * Quem pode classificar e registrar à mão. Só esconde botão: quem decide
     * de verdade é a RLS (pode_registrar_interrupcao_geradora).
     */
    podeRegistrar: boolean;
}

// ---------------- Aparência por tipo ----------------

const ESTILO_TIPO: Record<TipoInterrupcao, { chip: string; icone: React.ReactNode }> = {
    parada: {
        chip: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800',
        icone: <PowerOff size={11} className="stroke-[2.5]" />,
    },
    parcial: {
        chip: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
        icone: <TrendingDown size={11} className="stroke-[2.5]" />,
    },
    sem_comunicacao: {
        chip: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800',
        icone: <WifiOff size={11} className="stroke-[2.5]" />,
    },
    sem_leitura: {
        chip: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600',
        icone: <CloudOff size={11} className="stroke-[2.5]" />,
    },
};

/** O que a medição diz do episódio, em uma frase. */
const descreverMedicao = (e: InterrupcaoDetectada): string => {
    const partes: string[] = [];
    const pelaFrota = e.referencia === 'frota' ? ' (estimativa pelas usinas de outros locais)' : '';
    if (e.tipo === 'parcial') {
        if (e.pct_da_referencia != null) partes.push(`Gerou ${e.pct_da_referencia.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% das usinas irmãs`);
        if (e.energia_nao_gerada_kwh != null) partes.push(`cerca de ${formatKwh(e.energia_nao_gerada_kwh)} a menos`);
        if (e.horas_equivalentes != null) partes.push(`equivale a ${formatHoras(e.horas_equivalentes)} parada`);
    } else if (e.tipo === 'parada') {
        if (e.a_confirmar) {
            partes.push('Sem geração registrada. Só dá para saber se ela gerou quando o dado voltar');
        } else if (e.energia_nao_gerada_kwh != null) {
            partes.push(`Cerca de ${formatKwh(e.energia_nao_gerada_kwh)} não gerados${pelaFrota}`);
        } else {
            partes.push('Sem geração registrada');
        }
    } else if (e.tipo === 'sem_comunicacao') {
        partes.push(e.pct_da_referencia != null
            ? `Sem dado no período; quando voltou, o acumulador trouxe cerca de ${Math.round(e.pct_da_referencia)}% do esperado`
            : 'Sem dado no período; a energia apareceu no acumulador quando o dado voltou');
    } else {
        partes.push(e.a_confirmar
            ? 'O coletor não gravou. Ainda sem dado para saber se a usina gerou'
            : e.pct_da_referencia != null
                ? `O coletor não gravou; a energia do período apareceu depois (cerca de ${Math.round(e.pct_da_referencia)}% do esperado)`
                : 'O coletor não gravou');
    }
    return `${partes.join(' · ')}.`;
};

// ---------------- Formulário (classificar / registrar à mão) ----------------

type Edicao =
    | { modo: 'classificar'; linha: LinhaInterrupcao }
    | { modo: 'manual'; registro: RegistroInterrupcao | null };

/** ISO → valor de <input type="datetime-local">, no relógio de quem digita. */
const paraCampoLocal = (iso: string | null): string => {
    if (!iso) return '';
    const d = new Date(iso);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const CAMPO = 'w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white outline-none focus:border-primary focus:ring-2 focus:ring-primary/20';
const ROTULO = 'block text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1';

const FormularioInterrupcao: React.FC<{
    edicao: Edicao;
    unidades: UnidadeGeradora[];
    nomePorSite: Record<string, string>;
    siteInicial: string | null;
    onFechar: () => void;
    onSalvo: () => void;
}> = ({ edicao, unidades, nomePorSite, siteInicial, onFechar, onSalvo }) => {
    const existente = edicao.modo === 'classificar' ? edicao.linha.registro : edicao.registro;
    const [site, setSite] = useState(existente?.site_id_api ?? siteInicial ?? unidades[0]?.site_id_api ?? '');
    const [tipo, setTipo] = useState<TipoInterrupcao>(existente?.tipo ?? 'parada');
    const [inicio, setInicio] = useState(paraCampoLocal(existente?.inicio ?? null));
    const [fim, setFim] = useState(paraCampoLocal(existente?.fim ?? null));
    const [motivo, setMotivo] = useState(existente?.motivo ?? '');
    const [observacao, setObservacao] = useState(existente?.observacao ?? '');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    const classificando = edicao.modo === 'classificar';
    const manualValido = !!site && !!inicio && (!fim || new Date(fim) > new Date(inicio));
    const podeSalvar = !!motivo && (classificando || manualValido) && !salvando;

    const salvar = async () => {
        setSalvando(true);
        setErro(null);
        try {
            if (edicao.modo === 'classificar') {
                const e = edicao.linha;
                await api.geradoras.salvarRegistroInterrupcao({
                    id: existente?.id,
                    site_id_api: e.site_id_api,
                    tipo: e.tipo,
                    origem: 'classificacao',
                    // O início do episódio é a âncora do casamento: não pode mudar ao editar.
                    inicio: existente?.inicio ?? e.inicio,
                    // Em aberto fica sem fim: o registro não pode "encerrar" o que ainda corre.
                    fim: e.detectada?.em_aberto ? null : e.fim,
                    motivo,
                    observacao: observacao.trim() || null,
                });
            } else {
                await api.geradoras.salvarRegistroInterrupcao({
                    id: existente?.id,
                    site_id_api: site,
                    tipo,
                    origem: 'manual',
                    inicio: new Date(inicio).toISOString(),
                    fim: fim ? new Date(fim).toISOString() : null,
                    motivo,
                    observacao: observacao.trim() || null,
                });
            }
            onSalvo();
        } catch (e: any) {
            setErro(e?.message || 'Não foi possível salvar o registro.');
            setSalvando(false);
        }
    };

    const remover = async () => {
        if (!existente) return;
        setSalvando(true);
        setErro(null);
        try {
            await api.geradoras.removerRegistroInterrupcao(existente.id);
            onSalvo();
        } catch (e: any) {
            setErro(e?.message || 'Não foi possível remover o registro.');
            setSalvando(false);
        }
    };

    const linha = classificando ? edicao.linha : null;

    return createPortal(
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-3 sm:p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={onFechar} />
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg max-h-[calc(100vh-2rem)] flex flex-col shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700 animate-modal-in">
                <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100 dark:border-gray-700 flex items-start justify-between gap-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-primary dark:text-primary-light font-bold">
                            {classificando ? 'Motivo do episódio' : 'Registro manual'}
                        </div>
                        <h3 className="text-xl font-poppins font-bold text-gray-900 dark:text-white mt-1">
                            {classificando ? nomePorSite[linha!.site_id_api] || linha!.site_id_api : existente ? 'Editar interrupção' : 'Registrar interrupção'}
                        </h3>
                        {linha ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {TIPO_INTERRUPCAO[linha.tipo].rotulo} · {rotuloPeriodo(linha)}
                            </p>
                        ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                Para o que a telemetria não enxerga: manutenção fora do horário de sol, desligamento avisado antes.
                            </p>
                        )}
                    </div>
                    <button type="button" onClick={onFechar} aria-label="Fechar"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <X size={18} />
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-4">
                    {!classificando && (
                        <>
                            <div>
                                <label className={ROTULO} htmlFor="int-usina">Usina</label>
                                <select id="int-usina" className={CAMPO} value={site} onChange={e => setSite(e.target.value)}>
                                    {unidades.map(u => (
                                        <option key={u.site_id_api} value={u.site_id_api}>{nomePorSite[u.site_id_api] || u.nome_usina}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={ROTULO} htmlFor="int-tipo">O que aconteceu</label>
                                <select id="int-tipo" className={CAMPO} value={tipo} onChange={e => setTipo(e.target.value as TipoInterrupcao)}>
                                    <option value="parada">Parada: a usina não gerou</option>
                                    <option value="parcial">Operação parcial: gerou com parte dos inversores</option>
                                </select>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className={ROTULO} htmlFor="int-inicio">Início</label>
                                    <input id="int-inicio" type="datetime-local" className={CAMPO} value={inicio} onChange={e => setInicio(e.target.value)} />
                                </div>
                                <div>
                                    <label className={ROTULO} htmlFor="int-fim">Fim (vazio se ainda não voltou)</label>
                                    <input id="int-fim" type="datetime-local" className={CAMPO} value={fim} onChange={e => setFim(e.target.value)} />
                                </div>
                            </div>
                            {fim && inicio && new Date(fim) <= new Date(inicio) && (
                                <p className="text-xs text-rose-600 dark:text-rose-400">O fim precisa ser depois do início.</p>
                            )}
                        </>
                    )}

                    <div>
                        <label className={ROTULO} htmlFor="int-motivo">Motivo</label>
                        <select id="int-motivo" className={CAMPO} value={motivo} onChange={e => setMotivo(e.target.value)}>
                            <option value="" disabled>Escolha o motivo</option>
                            {MOTIVOS_INTERRUPCAO.map(m => <option key={m.id} value={m.id}>{m.rotulo}</option>)}
                            {/* Motivo antigo que saiu da lista continua editável sem ser perdido. */}
                            {motivo && !MOTIVOS_INTERRUPCAO.some(m => m.id === motivo) && <option value={motivo}>{motivo}</option>}
                        </select>
                        {motivo && getMotivo(motivo)?.contaComoPerda === false && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                                Com este motivo o episódio continua na lista, mas sai dos totais de energia não gerada.
                            </p>
                        )}
                    </div>
                    <div>
                        <label className={ROTULO} htmlFor="int-obs">Observação</label>
                        <textarea id="int-obs" rows={3} className={CAMPO} value={observacao} onChange={e => setObservacao(e.target.value)}
                            placeholder="O que foi apurado: qual inversor, protocolo da distribuidora, quem atendeu." />
                    </div>

                    {erro && (
                        <div className="rounded-xl border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
                            {erro}
                        </div>
                    )}
                </div>

                <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
                    {existente ? (
                        <button type="button" onClick={remover} disabled={salvando}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-600 dark:text-rose-400 hover:underline disabled:opacity-50">
                            <Trash2 size={14} />
                            {classificando ? 'Remover motivo' : 'Excluir registro'}
                        </button>
                    ) : <span />}
                    <div className="flex items-center gap-2">
                        <button type="button" onClick={onFechar}
                            className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                            Cancelar
                        </button>
                        <button type="button" onClick={salvar} disabled={!podeSalvar}
                            className="px-4 py-2 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

// ---------------- Por que gerou menos ----------------

const SEM_COMPARACAO: Record<MotivoSemComparacao, { texto: string; dica: string }> = {
    avulsa: { texto: '—', dica: 'Usina sem irmãs no mesmo conjunto: não há com quem comparar.' },
    sem_irmas: { texto: 'sem irmã com dado', dica: 'Nenhuma outra usina do conjunto tem dado comparável neste período.' },
    sem_capacidade: { texto: 'sem MWp cadastrado', dica: 'Sem capacidade cadastrada não dá para pôr na mesma régua (kWh por kWp).' },
    sem_geracao: { texto: 'sem geração no período', dica: 'Nenhuma geração registrada no período.' },
    acumulador: { texto: 'acumulador saltou', dica: 'O acumulador de energia deu um salto impossível num dia do período. O total dela não é confiável, então ela não entra na comparação nem serve de referência.' },
};

/**
 * Sobra a partir da qual vale chamar atenção: 5% do que a usina geraria no
 * ritmo da melhor irmã. Abaixo disso é a dispersão normal entre usinas iguais
 * (em set/2026 o Bloco 4, sem problema nenhum, ficou 3,1% atrás do Bloco 5).
 */
const SOBRA_QUE_PEDE_OLHO_PCT = 5;

const pct = (v: number) => `${v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

const descreverInterrupcoes = (r: ResumoInterrupcoesUsina | undefined): string => {
    if (!r) return '—';
    const partes: string[] = [];
    // registro manual não tem medição: conta o episódio, sem inventar horas
    if (r.paradas) partes.push(`${r.paradas} ${r.paradas === 1 ? 'parada' : 'paradas'}${r.horasParada > 0 ? ` · ${formatHoras(r.horasParada)} de sol` : ''}`);
    if (r.diasParcial) partes.push(`${r.diasParcial} ${r.diasParcial === 1 ? 'dia' : 'dias'} em parcial`);
    return partes.join(' · ') || '—';
};

const TH = 'py-2 px-3 font-bold text-gray-400 uppercase text-[10px] tracking-wider';

const TabelaPorQueGerouMenos: React.FC<{
    linhas: LinhaDecomposicao[];
    resumoPorSite: Map<string, ResumoInterrupcoesUsina>;
    nomePorSite: Record<string, string>;
    comComparacao: boolean;
}> = ({ linhas, resumoPorSite, nomePorSite, comComparacao }) => {
    let grupoAnterior: string | null | undefined;
    return (
        <div className="mt-4">
            <h3 className="font-poppins font-bold text-sm text-gray-900 dark:text-white">Por que gerou menos</h3>
            <div className="mt-1 overflow-x-auto">
                <table className="w-full text-left text-sm whitespace-nowrap">
                    <thead>
                        <tr className="border-b border-gray-100 dark:border-gray-700">
                            <th className={`${TH} pl-0`}>Usina</th>
                            <th className={`${TH} text-right`}>Gerou</th>
                            {comComparacao && <th className={`${TH} text-right`}>Abaixo da melhor irmã</th>}
                            <th className={TH}>Interrupções</th>
                            <th className={`${TH} text-right`}>Não gerado nelas</th>
                            {comComparacao && <th className={`${TH} text-right`}>Explicam da diferença</th>}
                            {comComparacao && <th className={`${TH} pr-0 text-right`}>Sem explicação</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {linhas.map(l => {
                            const r = resumoPorSite.get(l.site_id_api);
                            const c = l.comparacao;
                            const grupo = l.conjunto?.id ?? null;
                            const novoGrupo = grupo !== grupoAnterior;
                            grupoAnterior = grupo;
                            const referencia = c ? nomePorSite[c.referenciaSite] || c.referenciaSite : null;
                            const sobraAlta = !!c && !c.ehReferencia && c.sobraPct >= SOBRA_QUE_PEDE_OLHO_PCT;
                            return (
                                <React.Fragment key={l.site_id_api}>
                                    {novoGrupo && comComparacao && (
                                        <tr>
                                            <td colSpan={7} className="pt-3 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-primary dark:text-primary-light">
                                                {l.conjunto ? l.conjunto.titulo : 'Sem irmãs para comparar'}
                                                {referencia && (
                                                    <span className="ml-2 font-semibold normal-case tracking-normal text-gray-400 dark:text-gray-500">
                                                        melhor por kWp: {referencia}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                    <tr className="border-t border-gray-100 dark:border-gray-700/50">
                                        <td className="py-2 pr-3 font-semibold text-gray-900 dark:text-white">
                                            {nomePorSite[l.site_id_api] || l.site_id_api}
                                            {l.diasSemDado > 0 && (
                                                <span className="ml-2 text-[11px] font-normal text-amber-600 dark:text-amber-400"
                                                    title="Menos dias de dado do que a referência: parte da diferença pode ser leitura faltando, não geração.">
                                                    {l.diasSemDado} {l.diasSemDado === 1 ? 'dia' : 'dias'} sem dado
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2 px-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                                            {l.geracaoKwh != null ? formatKwh(l.geracaoKwh) : '—'}
                                        </td>
                                        {comComparacao && (
                                            <td className="py-2 px-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                                                {c ? (c.ehReferencia
                                                    ? <span className="text-gray-400 dark:text-gray-500">referência</span>
                                                    : <>{formatKwh(c.abaixoKwh)} <span className="text-gray-400 dark:text-gray-500">· {pct(c.abaixoPct)}</span></>)
                                                    : <span className="text-gray-400 dark:text-gray-500" title={SEM_COMPARACAO[l.semComparacao || 'avulsa'].dica}>
                                                        {SEM_COMPARACAO[l.semComparacao || 'avulsa'].texto}
                                                    </span>}
                                            </td>
                                        )}
                                        <td className="py-2 px-3 text-gray-700 dark:text-gray-200">{descreverInterrupcoes(r)}</td>
                                        <td className="py-2 px-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                                            {r && r.energiaNaoGeradaKwh > 0 ? formatKwh(r.energiaNaoGeradaKwh) : '—'}
                                        </td>
                                        {comComparacao && (
                                            <td className="py-2 px-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                                                {c && !c.ehReferencia && c.explicadoKwh >= 1 ? formatKwh(c.explicadoKwh) : '—'}
                                            </td>
                                        )}
                                        {comComparacao && (
                                            <td className={`py-2 pl-3 text-right tabular-nums ${sobraAlta ? 'font-bold text-amber-700 dark:text-amber-300' : 'text-gray-700 dark:text-gray-200'}`}
                                                title={sobraAlta ? 'Nenhuma interrupção explica esta parte. Vale olhar o rendimento: sujeira, string fora, inversor limitado — ou a capacidade cadastrada.' : undefined}>
                                                {c && !c.ehReferencia && c.abaixoKwh > 0
                                                    ? <>{formatKwh(c.sobraKwh)} <span className={sobraAlta ? '' : 'text-gray-400 dark:text-gray-500'}>· {pct(c.sobraPct)}</span></>
                                                    : '—'}
                                            </td>
                                        )}
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ---------------- Bloco ----------------

const Numero: React.FC<{ titulo: string; valor: string; unidade?: string; rodape: string }> = ({ titulo, valor, unidade, rodape }) => (
    <div className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/30 px-4 py-3">
        <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-bold">{titulo}</div>
        <div className="mt-1 text-2xl font-montserrat font-bold text-gray-900 dark:text-white tabular-nums">
            {valor}
            {unidade ? <span className="text-sm text-gray-400 font-semibold ml-1.5">{unidade}</span> : null}
        </div>
        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{rodape}</div>
    </div>
);

const Interrupcoes: React.FC<Props> = ({ unidades, site, janela, geracao, nomePorSite, podeRegistrar }) => {
    const [detectadas, setDetectadas] = useState<InterrupcaoDetectada[]>([]);
    const [registros, setRegistros] = useState<RegistroInterrupcao[]>([]);
    const [catalogo, setCatalogo] = useState<AlarmeCatalogo[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [mostrarSemDado, setMostrarSemDado] = useState(false);
    const [edicao, setEdicao] = useState<Edicao | null>(null);
    const [versao, setVersao] = useState(0);

    useEffect(() => {
        api.geradoras.getCatalogoAlarmes().then(setCatalogo).catch(() => setCatalogo([]));
    }, []);

    useEffect(() => {
        let vivo = true;
        setCarregando(true);
        setErro(null);
        Promise.all([
            // Sempre TODAS as usinas, mesmo com uma só no seletor: para dizer
            // quanto da diferença a interrupção explica é preciso saber o que
            // a melhor irmã também perdeu. O filtro por usina é feito aqui.
            api.geradoras.getInterrupcoes(janela.inicio, janela.fim),
            // O registro é opcional: quem não tem acesso a ele (ou o monitor
            // externo) ainda vê o que a telemetria detectou.
            api.geradoras.getRegistrosInterrupcao(janela.inicio, janela.fim).catch(() => [] as RegistroInterrupcao[]),
        ])
            .then(([d, r]) => { if (vivo) { setDetectadas(d); setRegistros(r); } })
            .catch(e => {
                if (!vivo) return;
                setDetectadas([]);
                setRegistros([]);
                // PGRST202 = função não existe no banco: a migration ainda não foi aplicada.
                setErro(e?.code === 'PGRST202'
                    ? 'A detecção de interrupções ainda não foi ativada no banco (migration 20261011120000).'
                    : e?.message || 'Não foi possível calcular as interrupções.');
            })
            .finally(() => { if (vivo) setCarregando(false); });
        return () => { vivo = false; };
    }, [janela, versao]);

    const indice = useMemo(() => indexarCatalogo(catalogo), [catalogo]);
    const linhasDeTodas = useMemo(() => casarRegistros(detectadas, registros, janela), [detectadas, registros, janela]);
    const resumoDeTodas = useMemo(() => resumirPorUsina(linhasDeTodas), [linhasDeTodas]);
    const linhas = useMemo(() => (site ? linhasDeTodas.filter(l => l.site_id_api === site) : linhasDeTodas), [linhasDeTodas, site]);
    const resumo = useMemo(() => (site ? resumoDeTodas.filter(r => r.site_id_api === site) : resumoDeTodas), [resumoDeTodas, site]);

    const resumoPorSite = useMemo(() => new Map(resumoDeTodas.map(r => [r.site_id_api, r])), [resumoDeTodas]);
    const decomposicao = useMemo(() => {
        const porSite = new Map(geracao.map(g => [g.site_id_api, g]));
        return decomporDiferenca(unidades.map(u => {
            const g = porSite.get(u.site_id_api);
            return {
                site_id_api: u.site_id_api,
                nome_usina: u.nome_usina,
                capacidade_instalada: u.capacidade_instalada,
                geracao_kwh: g?.geracao_kwh ?? null,
                melhor_dia_kwh: g?.melhor_dia_kwh ?? null,
                dias_com_dado: g?.dias_com_dado ?? 0,
            };
        }), resumoDeTodas);
    }, [unidades, geracao, resumoDeTodas]);
    // Na tabela: a usina do seletor, ou todas as de conjunto mais as avulsas que tiveram perda.
    const linhasDaTabela = useMemo(() => decomposicao.filter(l => {
        if (site) return l.site_id_api === site;
        if (l.conjunto) return true;
        const r = resumoPorSite.get(l.site_id_api);
        return !!r && (r.energiaNaoGeradaKwh > 0 || r.paradas > 0 || r.diasParcial > 0);
    }), [decomposicao, site, resumoPorSite]);

    const perdas = linhas.filter(l => TIPO_INTERRUPCAO[l.tipo].ehPerda);
    const semDado = linhas.filter(l => !TIPO_INTERRUPCAO[l.tipo].ehPerda);
    const visiveis = mostrarSemDado ? linhas : perdas;
    const total = resumo.reduce((t, r) => ({
        energia: t.energia + r.energiaNaoGeradaKwh,
        horas: t.horas + r.horasEquivalentes,
        semMotivo: t.semMotivo + r.semMotivo,
        desconsiderados: t.desconsiderados + r.desconsiderados,
        frota: t.frota || r.temReferenciaFrota,
    }), { energia: 0, horas: 0, semMotivo: 0, desconsiderados: 0, frota: false });
    const nParadas = perdas.filter(l => l.tipo === 'parada' && contaComoPerda(l)).length;
    const nParciais = perdas.filter(l => l.tipo === 'parcial' && contaComoPerda(l)).length;

    const fecharESalvar = useCallback(() => { setEdicao(null); setVersao(v => v + 1); }, []);
    const [energiaValor, energiaUnidade] = formatKwh(total.energia).split(' ');

    return (
        <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div>
                    <div className="flex items-center gap-2">
                        <h2 className="font-poppins font-bold text-gray-900 dark:text-white">Interrupções e horas paradas</h2>
                        {carregando && <RefreshCw size={14} className="animate-spin text-primary" />}
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                        {janela.label} · detectadas pela telemetria; o motivo é de quem apurou
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {semDado.length > 0 && (
                        <button
                            type="button"
                            onClick={() => setMostrarSemDado(v => !v)}
                            aria-pressed={mostrarSemDado}
                            title="Falta de comunicação e de leitura: a usina gerou, mas ficou sem dado"
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${mostrarSemDado
                                ? 'bg-sky-50 dark:bg-sky-900/30 border-sky-200 dark:border-sky-800 text-sky-700 dark:text-sky-300'
                                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'}`}
                        >
                            <WifiOff size={12} />
                            {mostrarSemDado ? 'Ocultar' : 'Mostrar'} falta de dado ({semDado.length})
                        </button>
                    )}
                    {podeRegistrar && !erro && (
                        <button
                            type="button"
                            onClick={() => setEdicao({ modo: 'manual', registro: null })}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-white text-xs font-semibold hover:bg-primary-dark transition-colors"
                        >
                            <Plus size={13} />
                            Registrar interrupção
                        </button>
                    )}
                </div>
            </div>

            {erro ? (
                <div className="rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50/70 dark:bg-amber-900/15 px-4 py-3 text-sm text-amber-800 dark:text-amber-200 flex gap-2">
                    <TriangleAlert size={16} className="flex-shrink-0 mt-0.5" />
                    {erro}
                </div>
            ) : carregando && linhas.length === 0 ? (
                <div className="space-y-2">
                    <div className="h-16 rounded-2xl bg-gray-100/80 dark:bg-gray-700/40 animate-pulse" />
                    <div className="h-12 rounded-2xl bg-gray-100/80 dark:bg-gray-700/40 animate-pulse" />
                    <p className="text-[11px] text-gray-400 dark:text-gray-500">Comparando cada usina com as outras, hora a hora…</p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Numero
                            titulo="Energia não gerada"
                            valor={energiaValor}
                            unidade={energiaUnidade}
                            rodape={total.frota
                                ? 'Estimativa; parte dela pela geração de usinas de outros locais'
                                : 'Estimativa contra as usinas irmãs no mesmo período'}
                        />
                        <Numero
                            titulo="Horas paradas (equivalentes)"
                            valor={formatHoras(total.horas).replace(' h', '')}
                            unidade="h"
                            rodape="Parada total, mais a operação parcial convertida em horas"
                        />
                        <Numero
                            titulo="Episódios"
                            valor={String(nParadas + nParciais)}
                            rodape={[
                                `${nParadas} ${nParadas === 1 ? 'parada' : 'paradas'}`,
                                `${nParciais} em operação parcial`,
                                total.semMotivo ? `${total.semMotivo} sem motivo` : null,
                                total.desconsiderados ? `${total.desconsiderados} desconsiderado${total.desconsiderados > 1 ? 's' : ''}` : null,
                            ].filter(Boolean).join(' · ')}
                        />
                    </div>

                    {linhasDaTabela.length > 0 && (
                        <TabelaPorQueGerouMenos
                            linhas={linhasDaTabela}
                            resumoPorSite={resumoPorSite}
                            nomePorSite={nomePorSite}
                            comComparacao={geracao.length > 0}
                        />
                    )}

                    {visiveis.length === 0 ? (
                        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                            Nenhuma parada nem operação parcial detectada em {janela.label.toLowerCase()}.
                            {semDado.length > 0 ? ` Houve ${semDado.length} ${semDado.length === 1 ? 'episódio' : 'episódios'} de falta de dado, sem perda de geração.` : ''}
                        </p>
                    ) : (
                        <ul className="mt-4 space-y-2">
                            {visiveis.map(l => {
                                const e = l.detectada;
                                const estilo = ESTILO_TIPO[l.tipo];
                                const motivo = getMotivo(l.registro?.motivo);
                                const fora = !contaComoPerda(l) && TIPO_INTERRUPCAO[l.tipo].ehPerda;
                                return (
                                    <li key={l.chave} className={`rounded-2xl border border-gray-100 dark:border-gray-700 px-4 py-3 ${fora ? 'opacity-70' : ''}`}>
                                        <div className="flex items-start justify-between gap-3 flex-wrap">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider ${estilo.chip}`}
                                                        title={TIPO_INTERRUPCAO[l.tipo].explicacao}>
                                                        {estilo.icone}
                                                        {TIPO_INTERRUPCAO[l.tipo].rotulo}
                                                    </span>
                                                    <span className="font-poppins font-bold text-gray-900 dark:text-white">{nomePorSite[l.site_id_api] || l.site_id_api}</span>
                                                    {e?.em_aberto && (
                                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider bg-rose-50 dark:bg-rose-900/25 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800">
                                                            Em andamento
                                                        </span>
                                                    )}
                                                    {!e && (
                                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600">
                                                            Registro manual
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 tabular-nums">
                                                    {rotuloPeriodo(l)}
                                                    {e ? ` · ${rotuloDuracao(e)}` : ''}
                                                    {/* a tabela de cima soma só o que cai no período; aqui o episódio aparece inteiro */}
                                                    {e && e.tipo !== 'parcial' && e.horas_sol_periodo < e.horas_sol - 0.5
                                                        ? ` (${formatHoras(e.horas_sol_periodo)} dentro do período)` : ''}
                                                    {e?.continua_de_antes ? ` · já vinha de antes de ${rotuloInstante(e.inicio)}` : ''}
                                                    {e?.continua_depois ? ' · segue além do período analisado' : ''}
                                                </div>
                                                {e && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{descreverMedicao(e)}</div>}
                                                {e && e.alarmes.length > 0 && (
                                                    <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                                                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500">
                                                            <Siren size={11} /> Alarmes perto do início:
                                                        </span>
                                                        {e.alarmes.map(a => (
                                                            <span key={`${a.fonte}|${a.error_code}|${a.error_code_info}`}
                                                                title={`${a.error_code}${a.error_code_info ? ` · ${a.error_code_info}` : ''} · primeiro em ${rotuloInstante(a.primeiro)}`}
                                                                className="px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-[11px] text-gray-600 dark:text-gray-300">
                                                                {tituloAlarme(a, buscarCatalogo(indice, a))}
                                                                <span className="text-gray-400 dark:text-gray-500"> · {a.inversores} inv.</span>
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                {l.registro?.observacao && (
                                                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-1.5 whitespace-pre-line">"{l.registro.observacao}"</p>
                                                )}
                                            </div>

                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                {motivo ? (
                                                    <span className="inline-flex flex-col items-end">
                                                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${motivo.contaComoPerda
                                                            ? 'bg-emerald-50 dark:bg-emerald-900/25 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600'}`}>
                                                            {motivo.rotulo}
                                                        </span>
                                                        {l.registro?.autor?.full_name && (
                                                            <span className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">por {l.registro.autor.full_name}</span>
                                                        )}
                                                    </span>
                                                ) : TIPO_INTERRUPCAO[l.tipo].ehPerda ? (
                                                    <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500">
                                                        <CircleHelp size={12} /> Sem motivo
                                                    </span>
                                                ) : null}
                                                {podeRegistrar && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setEdicao(e ? { modo: 'classificar', linha: l } : { modo: 'manual', registro: l.registro })}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-600 text-[11px] font-semibold text-gray-700 dark:text-gray-200 hover:border-primary hover:text-primary transition-colors"
                                                    >
                                                        <Pencil size={11} />
                                                        {l.registro ? 'Editar' : 'Classificar'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    <p className="mt-3 text-[11px] text-gray-400 dark:text-gray-500">
                        Parada = o acumulador de energia não andou enquanto as outras usinas geravam, e a energia não apareceu depois.
                        Operação parcial = a usina gerou menos de 85% das irmãs no dia. A energia não gerada é estimativa, não medição.
                        A detecção é por usina: qual inversor falhou sai dos alarmes e de quem foi a campo.
                    </p>
                    <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                        Abaixo da melhor irmã = o que a usina teria gerado a mais com o rendimento (kWh por kWp) da melhor do conjunto.
                        "Explicam" conta só a perda além da que a própria referência teve: parada que derruba o conjunto inteiro tira
                        geração de todas por igual e não cria diferença entre elas. O que sobra sem explicação é rendimento — ou
                        capacidade cadastrada errada, que distorce a régua.
                    </p>
                </>
            )}

            {edicao && (
                <FormularioInterrupcao
                    edicao={edicao}
                    unidades={unidades}
                    nomePorSite={nomePorSite}
                    siteInicial={site}
                    onFechar={() => setEdicao(null)}
                    onSalvo={fecharESalvar}
                />
            )}
        </div>
    );
};

export default Interrupcoes;
